// Packages and components
import React, { useContext, useCallback, useEffect, useState } from 'react';
import { Box } from 'grommet';
import { FormClose } from 'grommet-icons';
import { TrayContainer, TrayControls, TrayBody, Tray } from './SHDTray.styles';

// Helpers and utlities
import { getComponentClassName } from '@theme/utils/data';
import { ResponsiveContext } from 'grommet';

// Types
import type { SHDTrayProps } from './SHDTray.d';
import type { LayerPositionType } from 'grommet';

const SHDTray: React.FC<SHDTrayProps> = ({ className, children, onClose }) => {
  // Constants
  const componentClassName = 'tray';

  // States
  const [utilityClassNames, setUtilityClassNames] = useState<string[]>();
  const [trayPosition, setTrayPosition] = useState<LayerPositionType>('bottom');

  // Hooks and contexts
  const viewportSize = useContext(ResponsiveContext);

  // Handlers
  const handleUtilityClassNameUpdates = useCallback(() => {
    const utilityClassArr: string[] = [];
    if (className) {
      className.split(' ').forEach((name) => {
        utilityClassArr.push(name);
      });
    }
    utilityClassArr.push(`is-${viewportSize}`);
    setUtilityClassNames(utilityClassArr);
  }, [className, viewportSize]);

  const handlePropUpdates = useCallback(() => {
    if (viewportSize === 'small' || viewportSize === 'medium') {
      setTrayPosition('bottom');
    }

    if (viewportSize === 'large' || viewportSize === 'xlarge') {
      setTrayPosition('center');
    }
  }, [viewportSize]);

  // Effects
  useEffect(() => {
    handleUtilityClassNameUpdates();
    handlePropUpdates();
  }, [handleUtilityClassNameUpdates, handlePropUpdates]);

  return (
    <Tray
      background="white"
      className={getComponentClassName(componentClassName, utilityClassNames)}
      full={false}
      onClickOutside={onClose}
      onEsc={onClose}
      position={trayPosition}
      responsive={false}
    >
      <TrayContainer
        className={getComponentClassName(
          `${componentClassName}__container`,
          utilityClassNames
        )}
      >
        <TrayControls
          className={getComponentClassName(
            `${componentClassName}__controls`,
            utilityClassNames
          )}
        >
          <Box
            flex={{ grow: 0 }}
            align="start"
            style={{ position: 'relative', transform: 'translateX(-4px)' }}
          ></Box>
          <Box
            flex={{ grow: 0 }}
            align="end"
            style={{ position: 'relative', top: '6px' }}
          >
            {onClose && (
              <Box
                aria-label="Close Modal"
                role="button"
                onClick={onClose}
                pad={{ horizontal: '1rem', vertical: '0.875rem' }}
              >
                <FormClose size="30px" color="black" />
              </Box>
            )}
          </Box>
        </TrayControls>
        <TrayBody
          className={getComponentClassName(
            `${componentClassName}__body`,
            utilityClassNames
          )}
        >
          {children}
        </TrayBody>
      </TrayContainer>
    </Tray>
  );
};

export default SHDTray;
